import type { ZudokuConfig, ZudokuPlugin } from "zudoku";
import { Landingpage } from "./src/Landingpage";
import SignatureGenerator from "./src/SignatureGenerator";
import { arpAuthPlugin } from "./src/arpAuthPlugin";

const commonPlugin: ZudokuPlugin = {
  getHead: () => (
    <>
      <link rel="stylesheet" href="/docs-theme.css" />
      <script src="/chat-widget.js" defer></script>
      <script src="/page-tools.js" defer></script>
    </>
  ),
  getMdxComponents: () => ({
    SignatureGenerator,
  }),
};

const config: ZudokuConfig = {
  metadata: {
    title: "ARP Digital",
    description:
      "Production APIs for remittance, wallet, checkout, payout, and trading workflows.",
    keywords: [
      "ARP Digital",
      "API documentation",
      "remittance API",
      "wallet API",
      "checkout API",
      "payout API",
      "trade API",
      "webhooks",
      "HMAC authentication",
    ],
    favicon: "/favicon.ico",
  },

  docs: {
    publishMarkdown: false,
    llms: {
      llmsTxt: false,
      llmsTxtFull: false,
    },
    defaultOptions: {
      toc: true,
      copyPage: true,
      disablePager: false,
      showLastModified: true,
      suggestEdit: {
        url: "https://github.com/ARP-Digital-Solutions/arp-digital-monorepo/edit/main/apps/api-docs/pages/",
        text: "Suggest an improvement to this page",
      },
    },
  },

  site: {
    showPoweredBy: false,
    logo: {
      src: {
        light: "/logo_dark.svg",
        dark: "/logo_light.svg",
      },
      width: 140,
      alt: "ARP Digital",
    },
    footer: {
      copyright: "© 2026 ARP Digital. All rights reserved.",
      links: [
        {
          label: "Product",
          items: [
            {
              label: "Remittance API",
              to: "/remittance-api",
            },
            {
              label: "Trade API",
              to: "/trade-api",
            },
            {
              label: "Checkout API",
              to: "/checkout-api",
            },
            {
              label: "Wallet API",
              to: "/wallet-api",
            },
            {
              label: "Payout API",
              to: "/payout-api",
            },
          ],
        },
        {
          label: "Documentation",
          items: [
            {
              label: "Getting Started",
              to: "/getting-started",
            },
            {
              label: "Authentication",
              to: "/authentication",
            },
            {
              label: "Webhooks",
              to: "/webhooks",
            },
            {
              label: "Platform Overview",
              to: "/platform-overview",
            },
            {
              label: "Fees & Processing",
              to: "/fees-and-processing",
            },
          ],
        },
        {
          label: "Resources",
          items: [
            {
              label: "API Catalog",
              to: "/api-catalog",
            },
            {
              label: "API Reference",
              to: "/api-reference",
            },
            {
              label: "llms.txt",
              href: "/llms.txt",
            },
            {
              label: "llms-full.txt",
              href: "/llms-full.txt",
            },
            {
              label: "GitHub",
              href: "https://github.com/ARP-Digital-Solutions/arp-digital-monorepo",
            },
          ],
        },
        {
          label: "Company",
          items: [
            {
              label: "Website",
              href: "https://arpdigital.io",
            },
            {
              label: "Support",
              href: "https://platform.arpdigital.io/support",
            },
          ],
        },
      ],
    },
  },

  search: {
    type: "pagefind",
  },

  plugins: [commonPlugin, arpAuthPlugin],

  navigation: [
    {
      type: "link",
      label: "Home",
      to: "/",
      icon: "home",
    },

    {
      type: "custom-page",
      path: "/",
      element: <Landingpage />,
    },

    {
      type: "category",
      label: "Getting Started",
      icon: "book-open",
      items: [
        {
          type: "category",
          label: "Introduction",
          icon: "info",
          items: [
            {
              type: "doc",
              file: "getting-started",
              label: "Welcome",
              icon: "rocket",
            },
            {
              type: "doc",
              file: "platform-overview",
              label: "Platform Overview",
              icon: "compass",
            },
            {
              type: "doc",
              file: "authentication",
              label: "Authentication",
              icon: "key",
            },
            {
              type: "doc",
              file: "errors-and-retries",
              label: "Errors and Retries",
              icon: "alert-circle",
            },
            {
              type: "doc",
              file: "webhooks",
              label: "Webhooks",
              icon: "webhook",
            },
            {
              type: "doc",
              file: "fees-and-processing",
              label: "Fees & Processing",
              icon: "clock",
            },
          ],
        },
        {
          type: "category",
          label: "Verification",
          icon: "shield-check",
          items: [
            {
              type: "doc",
              file: "recipient-verification",
              label: "Recipient",
              icon: "users",
            },
            {
              type: "doc",
              file: "remittance-verification",
              label: "Remittance",
              icon: "send",
            },

            {
              type: "category",
              label: "Bank Account",
              icon: "building-2",
              items: [
                {
                  type: "doc",
                  file: "bank-verification-bhd",
                  label: "BHD - Bahrain Dinar",
                  icon: "circle",
                },
                {
                  type: "doc",
                  file: "bank-verification-sar",
                  label: "SAR - Saudi Riyal",
                  icon: "circle",
                },
                {
                  type: "doc",
                  file: "bank-verification-aed",
                  label: "AED - UAE Dirham",
                  icon: "circle",
                },
                {
                  type: "doc",
                  file: "bank-verification-usd",
                  label: "USD - US Dollar",
                  icon: "circle",
                },
              ],
            },
          ],
        },
        {
          type: "doc",
          file: "error-codes",
          label: "Error Codes",
          icon: "alert-circle",
        },
      ],
    },

    {
      type: "category",
      label: "API References",
      icon: "folder-cog",
      items: [
        {
          type: "doc",
          file: "api-reference",
          label: "Overview",
          icon: "book-open",
        },
        {
          type: "link",
          label: "Remittance API",
          to: "/remittance-api",
          icon: "dollar-sign",
        },
        {
          type: "link",
          label: "Trade API",
          to: "/trade-api",
          icon: "chart-line",
        },
        {
          type: "link",
          label: "Checkout API",
          to: "/checkout-api",
          icon: "credit-card",
        },
        {
          type: "link",
          label: "Wallet API",
          to: "/wallet-api",
          icon: "wallet",
        },
        {
          type: "link",
          label: "Payout API",
          to: "/payout-api",
          icon: "banknote",
        },
      ],
    },
    {
      type: "link",
      label: "API Catalog",
      to: "/api-catalog",
      icon: "grid",
    },
  ],

  catalogs: {
    path: "api-catalog",
    label: "API Catalog",
  },

  defaults: {
    apis: {
      examplesLanguage: "shell",
      supportedLanguages: [
        { value: "shell", label: "cURL" },
        { value: "javascript", label: "JavaScript" },
        { value: "python", label: "Python" },
      ],
      expandAllTags: false,
      expandApiInformation: true,
      schemaDownload: {
        enabled: true,
      },
    },
  },

  apis: [
    {
      type: "file",
      input: "./schema/remittance-openapi.json",
      path: "remittance-api",
    },
    {
      type: "file",
      input: "./schema/trade-openapi.json",
      path: "trade-api",
    },
    {
      type: "file",
      input: "./schema/checkout-openapi.json",
      path: "checkout-api",
    },
    {
      type: "file",
      input: "./schema/wallet-openapi.json",
      path: "wallet-api",
    },
    {
      type: "file",
      input: "./schema/payout-openapi.json",
      path: "payout-api",
    },
  ],
};

export default config;
